import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Search, Loader2, CheckCircle, UserPlus, IdCard, Mail, Phone, AlertCircle, X,
} from 'lucide-react';
import { useProtectedFetch } from '../hooks/useProtectedFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable Member Lookup — unified UI for finding an existing parent/member
 * via membership number, email, OR phone.
 *
 * Props:
 *  - onMatch(parent, children, membershipNumber)  Called when parent picks a match
 *  - onSkip()  Called when parent clicks "Continue as new registration"
 *  - showChildren: bool  Whether to surface children as click-to-select buttons (default true)
 *  - compact: bool  Shorter UI for embedded use
 */
export default function MemberLookup({ onMatch, onSkip, showChildren = true, compact = false }) {
  const protectedFetch = useProtectedFetch();
  const [membership, setMembership] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [matches, setMatches] = useState([]);
  const [chosenMatchIdx, setChosenMatchIdx] = useState(null);
  const [skipped, setSkipped] = useState(false);

  const handleLookup = async (e) => {
    e?.preventDefault();
    setError('');
    setMatches([]);
    setChosenMatchIdx(null);

    const member = membership.trim();
    const em = email.trim();
    const ph = phone.trim();

    if (!member && !em && !ph) {
      setError('Please enter your membership number, email, or mobile.');
      return;
    }

    setLoading(true);
    try {
      // Try membership number first (exact match)
      if (member) {
        const res = await protectedFetch(`${API_URL}/api/academy/lookup-member/${encodeURIComponent(member)}`);
        if (res.ok) {
          const data = await res.json();
          if (data?.found !== false && (data?.parent || data?.child)) {
            const parent = data.parent || {};
            const children = data.child ? [data.child] : (data.children || []);
            // Single exact match — bubble up immediately
            onMatch(parent, children, member);
            setLoading(false);
            return;
          }
          if (data?.found === false) {
            setError('No member found with that membership number. Try email or mobile instead.');
            setLoading(false);
            return;
          }
        } else if (res.status === 404) {
          setError('No member found with that membership number. Try email or mobile instead.');
          setLoading(false);
          return;
        } else if (res.status === 429) {
          setError('Too many lookups from this device. Please wait about a minute and try again.');
          setLoading(false);
          return;
        } else if (res.status === 400 || res.status === 403) {
          setError('Bot check failed. Please refresh the page and try again.');
          setLoading(false);
          return;
        } else {
          setError('Lookup service unavailable. Please try again shortly.');
          setLoading(false);
          return;
        }
      }

      // Fall back to email / phone via verify-parent — but only if the user
      // actually supplied one. Otherwise we'd fire a useless 400.
      if (!em && !ph) {
        setError('Please enter an email or mobile number to search.');
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      if (em) params.append('email', em);
      if (ph) params.append('phone', ph);
      const res = await protectedFetch(`${API_URL}/api/academy/verify-parent?${params.toString()}`);
      if (res.status === 429) {
        setError('Too many lookups from this device. Please wait about a minute and try again.');
        setLoading(false);
        return;
      }
      if (res.status === 400 || res.status === 403) {
        setError('Bot check failed. Please refresh the page and try again.');
        setLoading(false);
        return;
      }
      if (!res.ok) {
        setError('Lookup service unavailable. Please try again shortly.');
        setLoading(false);
        return;
      }
      const data = await res.json();
      if (!data?.found || !Array.isArray(data.matches) || data.matches.length === 0) {
        setError('No existing account found. You can continue and fill the form as a new registration.');
        setLoading(false);
        return;
      }
      // Single match → auto-select. Multiple → let user pick.
      if (data.matches.length === 1) {
        const m = data.matches[0];
        onMatch(m.parent, m.children || [], m.parent?.membership_number || '');
      } else {
        setMatches(data.matches);
      }
    } catch (err) {
      setError('Could not reach the lookup service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePickMatch = (idx) => {
    setChosenMatchIdx(idx);
    const m = matches[idx];
    onMatch(m.parent, m.children || [], m.parent?.membership_number || '');
  };

  const handleSkip = () => {
    setSkipped(true);
    onSkip?.();
  };

  if (skipped) {
    return (
      <Card className="bg-charcoal border-border" data-testid="member-lookup-skipped">
        <CardContent className="p-4 flex items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <UserPlus className="h-4 w-4 text-primary" />
            Continuing as a new registration.
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setSkipped(false)}
            className="text-xs text-primary hover:text-primary/80"
            data-testid="member-lookup-undo-skip"
          >
            <X className="h-3 w-3 mr-1" /> Change
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-card gold-border" data-testid="member-lookup">
      <CardHeader className={compact ? 'pb-3' : ''}>
        <CardTitle className="font-display text-lg text-primary flex items-center gap-2">
          <Search className="h-5 w-5" /> Already a member or registered before?
        </CardTitle>
        <CardDescription>
          Find your account quickly using any one of the options below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleLookup} className="space-y-3" noValidate>
          <div className="grid md:grid-cols-3 gap-3">
            <LookupField
              id="lookup-membership"
              icon={<IdCard className="h-4 w-4" />}
              label="Membership #"
              placeholder="e.g. ACJ0042"
              value={membership}
              onChange={setMembership}
              testid="lookup-membership-input"
            />
            <LookupField
              id="lookup-email"
              icon={<Mail className="h-4 w-4" />}
              label="Email"
              placeholder="parent@example.com"
              value={email}
              onChange={setEmail}
              type="email"
              testid="lookup-email-input"
            />
            <LookupField
              id="lookup-phone"
              icon={<Phone className="h-4 w-4" />}
              label="Mobile"
              placeholder="+65 9123 4567"
              value={phone}
              onChange={setPhone}
              testid="lookup-phone-input"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              type="submit"
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90 flex-1"
              data-testid="lookup-find-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              {loading ? 'Searching...' : 'Find My Account'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleSkip}
              className="border-border"
              data-testid="lookup-skip-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              I'm New — Skip
            </Button>
          </div>
        </form>

        {error && (
          <div
            className="flex items-start gap-2 text-sm text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded p-3"
            data-testid="lookup-error"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <AnimatePresence>
          {matches.length > 1 && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-2"
              data-testid="lookup-multiple-matches"
            >
              <p className="text-sm text-muted-foreground">
                We found {matches.length} possible matches — please pick yours:
              </p>
              {matches.map((m, idx) => (
                <MatchOption
                  key={m.parent?.id || idx}
                  match={m}
                  selected={chosenMatchIdx === idx}
                  onClick={() => handlePickMatch(idx)}
                  showChildren={showChildren}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

function LookupField({ id, icon, label, placeholder, value, onChange, type = 'text', testid }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
        {icon} {label}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}

function MatchOption({ match, selected, onClick, showChildren }) {
  const p = match.parent || {};
  const children = match.children || [];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-all ${
        selected
          ? 'bg-primary/15 border-primary'
          : 'bg-charcoal border-border hover:border-primary/50'
      }`}
      data-testid={`lookup-match-${p.id || 'unknown'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-card-foreground flex items-center gap-2 flex-wrap">
            {p.name || 'Unknown'}
            {p.is_scc_member && <Badge className="bg-primary/20 text-primary border-0 text-xs">SCC Member</Badge>}
            {p.membership_number && (
              <Badge variant="outline" className="text-xs font-mono">{p.membership_number}</Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {p.email && <span className="mr-3">{p.email}</span>}
            {p.phone && <span>{p.phone}</span>}
          </div>
          {showChildren && children.length > 0 && (
            <div className="text-xs text-muted-foreground mt-2">
              <span className="font-medium text-card-foreground">Children:</span>{' '}
              {children.map((c) => c.name).join(', ')}
            </div>
          )}
        </div>
        {selected && <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />}
      </div>
    </button>
  );
}

/**
 * Compact success banner — render this above your form once `onMatch` fires
 * so parents see clear confirmation of who AS thinks they are.
 */
export function MemberMatchedBanner({ parent, onChange }) {
  if (!parent) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-green-500/10 border border-green-500/40 rounded-lg p-4 flex items-start gap-3"
      data-testid="member-matched-banner"
    >
      <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-green-300">Welcome back, {parent.name}!</div>
        <div className="text-xs text-muted-foreground mt-1">
          {parent.membership_number && <span className="font-mono mr-2">Membership: {parent.membership_number}</span>}
          {parent.is_scc_member && <Badge className="bg-primary/20 text-primary border-0 text-xs">SCC Member</Badge>}
        </div>
      </div>
      {onChange && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onChange}
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="member-matched-change"
        >
          Not me?
        </Button>
      )}
    </motion.div>
  );
}
